import React, { useContext, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Menu, X, User, ChevronDown } from 'lucide-react';
import { BsFacebook, BsInstagram, BsWhatsapp } from 'react-icons/bs';
import { Store } from '../../Store';
import _settings from '../../services/settings.service';
import { hasPublicRichText, logoutHandler } from '../../utils';
import { isAdminPath, paths } from '../../routes/paths';
import { useTranslation } from 'react-i18next';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_SUCCESS':
      return {
        ...state,
        logoURL: action.payload.image?.fileLink || '',
        companyName: action.payload.companyName || '',
        whatsappUrl: action.payload.whatsappUrl || '',
        facebookUrl: action.payload.facebookUrl || '',
        instagramUrl: action.payload.instagramUrl || '',
        showContact: hasPublicRichText(action.payload.contactPageHtml),
      };
    default:
      return state;
  }
};

const INITIAL = {
  logoURL: '',
  companyName: '',
  whatsappUrl: '',
  facebookUrl: '',
  instagramUrl: '',
  showContact: false,
};

export default function Navbarr() {
  const { t } = useTranslation();
  const [settings, dispatch] = useReducer(reducer, INITIAL);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const location = useLocation();

  /* Close user-menu on outside click */
  useLayoutEffect(() => {
    const handler = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Close mobile menu on route change */
  useLayoutEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  /* Fetch brand settings */
  useLayoutEffect(() => {
    _settings
      .getPublicSettings()
      .then(data => dispatch({ type: 'FETCH_SUCCESS', payload: data }))
      .catch(() => {});
  }, []);

  /* Don't render in the admin shell */
  if (isAdminPath(location.pathname)) return null;

  const navLinkClass = ({ isActive }) =>
    [
      'text-sm transition-opacity whitespace-nowrap',
      isActive ? 'font-semibold opacity-100' : 'font-normal opacity-75 hover:opacity-100',
    ].join(' ');

  const hasSocials = settings.facebookUrl || settings.instagramUrl || settings.whatsappUrl;

  return (
    <>
      <ToastContainer position='bottom-center' limit={1} />

      {/* ── Fixed header shell ── */}
      <header
        style={{
          backgroundColor: 'var(--primaryColor)',
          color: 'var(--navFontColor)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {/* ── Main row ── */}
        <div
          className='mx-auto flex w-full items-center justify-between gap-4 px-6'
          style={{ height: '80px', maxWidth: '1280px' }}
        >
          {/* ── LEFT: Logo + nav links ─────────────────────────── */}
          <div className='flex items-center gap-8 min-w-0'>
            {/* Logo */}
            <Link to='/' className='shrink-0'>
              {settings.logoURL ? (
                <img
                  src={settings.logoURL}
                  alt={settings.companyName}
                  style={{ height: '50px', width: 'auto', objectFit: 'contain' }}
                />
              ) : (
                <span
                  className='text-xl font-bold tracking-tight'
                  style={{ color: 'var(--navFontColor)' }}
                >
                  {settings.companyName || 'gotalottery'}
                </span>
              )}
            </Link>

            {/* Nav links — desktop, left-aligned after the logo */}
            <nav className='hidden items-center gap-6 lg:flex'>
              <NavLink to='/' end className={navLinkClass} style={{ color: 'var(--navFontColor)' }}>
                {t('nav.home')}
              </NavLink>
              {settings.showContact && (
                <NavLink
                  to='/contact'
                  className={navLinkClass}
                  style={{ color: 'var(--navFontColor)' }}
                >
                  {t('nav.contact')}
                </NavLink>
              )}
              <NavLink to='/faq' className={navLinkClass} style={{ color: 'var(--navFontColor)' }}>
                {t('nav.faq')}
              </NavLink>
            </nav>
          </div>

          {/* ── RIGHT: social icons + user/avatar + hamburger ──── */}
          <div className='flex shrink-0 items-center gap-3'>
            {/* Social icons — desktop only */}
            {hasSocials && (
              <div className='hidden items-center gap-3 lg:flex'>
                {settings.facebookUrl && (
                  <a
                    href={settings.facebookUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='opacity-70 hover:opacity-100 transition-opacity'
                    style={{ color: 'var(--navFontColor)' }}
                    aria-label='Facebook'
                  >
                    <BsFacebook size={18} />
                  </a>
                )}
                {settings.instagramUrl && (
                  <a
                    href={settings.instagramUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='opacity-70 hover:opacity-100 transition-opacity'
                    style={{ color: 'var(--navFontColor)' }}
                    aria-label='Instagram'
                  >
                    <BsInstagram size={18} />
                  </a>
                )}
                {settings.whatsappUrl && (
                  <a
                    href={settings.whatsappUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='opacity-70 hover:opacity-100 transition-opacity'
                    style={{ color: 'var(--navFontColor)' }}
                    aria-label='WhatsApp'
                  >
                    <BsWhatsapp size={18} />
                  </a>
                )}

                {/* Divider between social icons and user area */}
                <span
                  className='h-5 w-px opacity-25'
                  style={{ backgroundColor: 'var(--navFontColor)' }}
                />
              </div>
            )}

            {/* ── User area ── */}
            {userInfo ? (
              <div className='relative' ref={userMenuRef}>
                {/* Avatar pill button */}
                <button
                  type='button'
                  onClick={() => setUserMenuOpen(prev => !prev)}
                  className='flex items-center gap-2 rounded-full px-2.5 py-1.5 text-sm transition-colors focus:outline-none'
                  style={{
                    color: 'var(--navFontColor)',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')
                  }
                >
                  {/* Initials circle */}
                  <span
                    className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold uppercase'
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      color: 'var(--navFontColor)',
                    }}
                  >
                    {userInfo.nombre?.charAt(0).toUpperCase() ?? <User size={13} />}
                  </span>
                  {/* Name — desktop only */}
                  <span className='hidden max-w-[110px] truncate font-medium lg:block'>
                    {userInfo.nombre}
                  </span>
                  <ChevronDown
                    size={13}
                    className={`shrink-0 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <div
                    className='absolute right-0 top-full mt-2 min-w-[160px] overflow-hidden rounded-xl bg-white py-1 shadow-xl ring-1 ring-black/5'
                    style={{ zIndex: 1100 }}
                  >
                    <Link
                      to='/profile'
                      className='block px-4 py-2.5 text-sm font-medium hover:bg-gray-50'
                      style={{ color: 'var(--primaryColor)' }}
                    >
                      {t('auth.myProfile')}
                    </Link>
                    {userInfo.isAdmin && (
                      <Link
                        to={paths.admin.root}
                        className='block px-4 py-2.5 text-sm font-medium hover:bg-gray-50'
                        style={{ color: 'var(--primaryColor)' }}
                      >
                        {t('nav.admin')}
                      </Link>
                    )}
                    <div className='my-1 h-px bg-gray-100' />
                    <button
                      type='button'
                      onClick={() => logoutHandler(ctxDispatch)}
                      className='block w-full px-4 py-2.5 text-left text-sm font-medium text-red-500 hover:bg-gray-50'
                    >
                      {t('auth.signOut')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Not logged in — sign-in link */
              <Link
                to='/signin'
                className='flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors'
                style={{
                  color: 'var(--navFontColor)',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                }}
              >
                <User size={15} />
                <span className='hidden lg:block'>{t('auth.signIn')}</span>
              </Link>
            )}

            {/* Mobile hamburger */}
            <button
              type='button'
              className='flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity lg:hidden'
              style={{ color: 'var(--navFontColor)', backgroundColor: 'transparent' }}
              onClick={() => setMobileOpen(prev => !prev)}
              aria-label='Toggle menu'
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* ── Mobile dropdown ── */}
        {mobileOpen && (
          <nav
            className='border-t lg:hidden'
            style={{
              borderColor: 'rgba(255,255,255,0.15)',
              backgroundColor: 'var(--primaryColor)',
            }}
          >
            <div className='mx-auto max-w-lg space-y-1 px-6 py-3'>
              {[
                { to: '/', label: t('nav.home'), end: true },
                settings.showContact ? { to: '/contact', label: t('nav.contact') } : null,
                { to: '/faq', label: t('nav.faq') },
                userInfo?.isAdmin ? { to: paths.admin.root, label: t('nav.admin') } : null,
              ]
                .filter(Boolean)
                .map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [
                        'block rounded-lg px-3 py-2.5 text-sm font-medium',
                        isActive
                          ? 'bg-white/20 font-semibold'
                          : 'opacity-80 hover:bg-white/10 hover:opacity-100',
                      ].join(' ')
                    }
                    style={{ color: 'var(--navFontColor)' }}
                  >
                    {item.label}
                  </NavLink>
                ))}

              {/* Social icons — mobile */}
              {hasSocials && (
                <div className='flex gap-4 px-3 pb-1 pt-3'>
                  {settings.facebookUrl && (
                    <a
                      href={settings.facebookUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ color: 'var(--navFontColor)' }}
                      className='opacity-70 hover:opacity-100 transition-opacity'
                      aria-label='Facebook'
                    >
                      <BsFacebook size={20} />
                    </a>
                  )}
                  {settings.instagramUrl && (
                    <a
                      href={settings.instagramUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ color: 'var(--navFontColor)' }}
                      className='opacity-70 hover:opacity-100 transition-opacity'
                      aria-label='Instagram'
                    >
                      <BsInstagram size={20} />
                    </a>
                  )}
                  {settings.whatsappUrl && (
                    <a
                      href={settings.whatsappUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ color: 'var(--navFontColor)' }}
                      className='opacity-70 hover:opacity-100 transition-opacity'
                      aria-label='WhatsApp'
                    >
                      <BsWhatsapp size={20} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </nav>
        )}
      </header>
    </>
  );
}
